const LOCK_NAME = "portail-pharma-ng-windows-tab-v1";
const CHANNEL_NAME = "portail-pharma-ng-windows-tab-v1";
const FALLBACK_OWNER_KEY = "portail-pharma-ng-windows-tab-owner-v1";
const FALLBACK_TTL_MS = 12_000;

export const WINDOWS_NIGHT_START_MINUTES = 22 * 60;
export const WINDOWS_NIGHT_END_MINUTES = 6 * 60 + 30;

export function isWindowsUserAgent(userAgent = "") {
  return /Windows NT/i.test(String(userAgent));
}

export function isWindowsNightClosureTime(date = new Date()) {
  const minutes = date.getHours() * 60 + date.getMinutes();
  return minutes >= WINDOWS_NIGHT_START_MINUTES || minutes < WINDOWS_NIGHT_END_MINUTES;
}

function closeOrBlank(delay = 180) {
  window.setTimeout(() => {
    try { window.close(); } catch { /* Chrome peut refuser un onglet ouvert manuellement. */ }
    window.setTimeout(() => {
      try { window.location.replace("about:blank"); } catch { /* L'écran neutre reste affiché. */ }
    }, delay);
  }, 0);
}

function safePortalUrl(value) {
  try {
    const target = new URL(String(value), window.location.origin);
    return target.origin === window.location.origin ? target.href : "";
  } catch {
    return "";
  }
}

function createChannel() {
  try { return typeof BroadcastChannel === "function" ? new BroadcastChannel(CHANNEL_NAME) : null; }
  catch { return null; }
}

function forwardToPrimary(channel) {
  channel?.postMessage({ type: "navigate", href: window.location.href });
}

function armNightClosure(onNight) {
  const check = () => {
    if (isWindowsNightClosureTime()) onNight();
  };
  const timer = window.setInterval(check, 30_000);
  check();
  return () => window.clearInterval(timer);
}

function startPrimaryChannel(channel, onNight) {
  if (channel) {
    channel.onmessage = (event) => {
      if (event?.data?.type !== "navigate") return;
      const href = safePortalUrl(event.data.href);
      if (href && href !== window.location.href) window.location.assign(href);
    };
  }
  const stopNight = armNightClosure(onNight);
  return () => {
    stopNight();
    channel?.close();
  };
}

async function acquireWebLock(channel, onNight) {
  let resolveResult;
  const result = new Promise((resolve) => { resolveResult = resolve; });
  let releaseLock;
  const hold = new Promise((resolve) => { releaseLock = resolve; });
  void navigator.locks.request(LOCK_NAME, { mode: "exclusive", ifAvailable: true }, async (lock) => {
    if (!lock) {
      forwardToPrimary(channel);
      resolveResult({ allowed: false, reason: "duplicate", close: () => closeOrBlank(2500) });
      return;
    }
    const dispose = startPrimaryChannel(channel, onNight);
    resolveResult({ allowed: true, dispose: () => { dispose(); releaseLock(); } });
    await hold;
  }).catch(() => resolveResult(null));
  return result;
}

async function acquireFallbackLock(channel, onNight) {
  const owner = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const readOwner = () => {
    try { return JSON.parse(window.localStorage.getItem(FALLBACK_OWNER_KEY) || "null"); }
    catch { return null; }
  };
  const current = readOwner();
  if (current?.id && Date.now() - Number(current.at || 0) < FALLBACK_TTL_MS) {
    forwardToPrimary(channel);
    return { allowed: false, reason: "duplicate", close: () => closeOrBlank(2500) };
  }
  const heartbeat = () => window.localStorage.setItem(FALLBACK_OWNER_KEY, JSON.stringify({ id: owner, at: Date.now() }));
  heartbeat();
  await new Promise((resolve) => window.setTimeout(resolve, 120));
  if (readOwner()?.id !== owner) {
    forwardToPrimary(channel);
    return { allowed: false, reason: "duplicate", close: () => closeOrBlank(2500) };
  }
  const interval = window.setInterval(heartbeat, 4_000);
  const stopPrimary = startPrimaryChannel(channel, onNight);
  const dispose = () => {
    window.clearInterval(interval);
    stopPrimary();
    if (readOwner()?.id === owner) window.localStorage.removeItem(FALLBACK_OWNER_KEY);
  };
  window.addEventListener("pagehide", dispose, { once: true });
  return { allowed: true, dispose };
}

export async function activateWindowsTabGuard() {
  if (!isWindowsUserAgent(window.navigator.userAgent)) return { allowed: true, reason: "non-windows" };
  if (isWindowsNightClosureTime()) return { allowed: false, reason: "night", close: () => closeOrBlank() };

  const channel = createChannel();
  const onNight = () => {
    channel?.postMessage({ type: "night-close" });
    closeOrBlank();
  };
  if (navigator.locks?.request) {
    const locked = await acquireWebLock(channel, onNight);
    if (locked) return locked;
  }
  return acquireFallbackLock(channel, onNight);
}

